"use client";

import { c as _c } from "react/compiler-runtime";
import type { Variants } from "framer-motion";
import type { HTMLAttributes } from "react";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";
import { m, useAnimation } from "framer-motion";
import { cn } from "@/lib/utils";
export interface ZapIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}
interface ZapIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
}
const PATH_VARIANTS: Variants = {
  normal: {
    opacity: 1,
    pathLength: 1,
    transition: {
      duration: 0.6,
      opacity: {
        duration: 0.1
      }
    }
  },
  animate: {
    opacity: [0, 1],
    pathLength: [0, 1],
    transition: {
      duration: 0.6,
      opacity: {
        duration: 0.1
      }
    }
  }
};
const ZapIcon = forwardRef<ZapIconHandle, ZapIconProps>((t0, ref) => {
  const $ = _c(30);
  let className;
  let onMouseEnter;
  let onMouseLeave;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      onMouseEnter,
      onMouseLeave,
      className,
      size: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onMouseEnter;
    $[3] = onMouseLeave;
    $[4] = props;
    $[5] = t1;
  } else {
    className = $[1];
    onMouseEnter = $[2];
    onMouseLeave = $[3];
    props = $[4];
    t1 = $[5];
  }
  const size = t1 === undefined ? 20 : t1;
  const controls = useAnimation();
  const isControlledRef = useRef(false);
  let t2;
  if ($[6] !== controls) {
    t2 = () => {
      isControlledRef.current = true;
      return {
        startAnimation: () => controls.start("animate"),
        stopAnimation: () => controls.start("normal")
      };
    };
    $[6] = controls;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  useImperativeHandle(ref, t2);
  let t3;
  if ($[8] !== controls || $[9] !== onMouseEnter) {
    t3 = e => {
      if (!isControlledRef.current) {
        controls.start("animate");
      }
      onMouseEnter?.(e);
    };
    $[8] = controls;
    $[9] = onMouseEnter;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleMouseEnter = t3;
  let t4;
  if ($[11] !== controls || $[12] !== onMouseLeave) {
    t4 = e_0 => {
      if (!isControlledRef.current) {
        controls.start("normal");
      }
      onMouseLeave?.(e_0);
    };
    $[11] = controls;
    $[12] = onMouseLeave;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const handleMouseLeave = t4;
  let t5;
  if ($[14] !== className) {
    t5 = cn("inline-flex items-center justify-center", className);
    $[14] = className;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== size) {
    t6 = {
      width: size,
      height: size
    };
    $[16] = size;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== controls) {
    t7 = <m.path variants={PATH_VARIANTS} initial="normal" animate={controls} d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />;
    $[18] = controls;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== size || $[21] !== t7) {
    t8 = <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{t7}</svg>;
    $[20] = size;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== handleMouseEnter || $[24] !== handleMouseLeave || $[25] !== props || $[26] !== t5 || $[27] !== t6 || $[28] !== t8) {
    t9 = <div className={t5} style={t6} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} {...props}>{t8}</div>;
    $[23] = handleMouseEnter;
    $[24] = handleMouseLeave;
    $[25] = props;
    $[26] = t5;
    $[27] = t6;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
});
ZapIcon.displayName = "ZapIcon";
export { ZapIcon };